/**
 * Where the assistant-connection surface learns whether Obsina has an MCP
 * endpoint at all.
 *
 * `apps/mcp` now exists, but an endpoint that is built does not mean an endpoint
 * that is published, so the page must never imply a working connection. The
 * single source of truth is the build-time public variable
 * `VITE_OBSINA_MCP_URL`, which an operator sets only once the endpoint is
 * deployed and reachable. An unset, malformed or non-HTTPS value resolves to
 * "not published" — the surface fails closed rather than printing an address
 * nobody can reach.
 *
 * The address is deliberately not constrained to a hostname. The server can be
 * published on its own vhost (`https://mcp.staging.obsina.com/mcp`) or under the
 * API's origin at a path (`https://api.staging.obsina.com/mcp`), which is the
 * deployment that needs no new DNS record. Both are whole URLs, and this
 * resolver treats them identically.
 *
 * This is a configuration claim, not an uptime claim: it says the deployment was
 * built pointing at that address, and the surface says nothing more than that.
 */

/** Only the keys this module reads; `import.meta.env` satisfies it structurally. */
export interface McpEndpointEnv {
  readonly VITE_OBSINA_MCP_URL?: string;
}

export type McpEndpoint =
  | { readonly published: true; readonly url: string }
  | { readonly published: false };

const LOOPBACK_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]"]);

/**
 * HTTPS is required for any real deployment. Plain HTTP is accepted only on the
 * loopback host, where a developer runs `apps/mcp` locally and no traffic leaves
 * the machine.
 */
function isUsableEndpoint(url: URL): boolean {
  if (url.protocol === "https:") return true;
  return url.protocol === "http:" && LOOPBACK_HOSTS.has(url.hostname);
}

/**
 * Reads the variable out of an untyped environment bag (`import.meta.env`),
 * narrowing it before it reaches the resolver — a non-string value is treated
 * exactly like an absent one.
 */
export function readMcpEndpoint(env: Record<string, unknown>): McpEndpoint {
  const raw = env.VITE_OBSINA_MCP_URL;
  return resolveMcpEndpoint({
    VITE_OBSINA_MCP_URL: typeof raw === "string" ? raw : undefined,
  });
}

export function resolveMcpEndpoint(env: McpEndpointEnv): McpEndpoint {
  const raw = env.VITE_OBSINA_MCP_URL?.trim();
  if (!raw) return { published: false };
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return { published: false };
  }
  if (!isUsableEndpoint(parsed)) return { published: false };
  return { published: true, url: parsed.toString() };
}
